import React, { useState, useContext, useRef, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Spin, Button, Image, Modal, Tooltip, message } from "antd";
import {
  ArrowUpOutlined, ArrowDownOutlined,
  PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined,
  AudioOutlined, PictureOutlined, EyeOutlined,
} from "@ant-design/icons";
import { API_DOMAIN } from "../constants";
import { fmtShortDateTime, getDocMeta } from "./helpers";
import { MediaScrollCtx, _mediaCache, _resolveMediaUrl } from "./MessageParts";

// ── Owner Badge ───────────────────────────────────────────────────────────────

export function OwnerBadge({ isOut }) {
  return (
    <Tooltip title={isOut ? "Sent" : "Received"}>
      <span style={{ position: "absolute", bottom: 5, left: 5, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "default" }}>
        {isOut
          ? <ArrowUpOutlined style={{ fontSize: 9, color: "#25d366" }} />
          : <ArrowDownOutlined style={{ fontSize: 9, color: "#8696a0" }} />}
      </span>
    </Tooltip>
  );
}

// ── Gallery Audio Card ────────────────────────────────────────────────────────

export function GalleryAudioCard({ src, ts, isOut }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const R = 34;
  const CX = 50;
  const CY = 50;
  const circumference = 2 * Math.PI * R;
  const pct = duration ? currentTime / duration : 0;
  const offset = circumference * (1 - pct);

  return (
    <div style={{ aspectRatio: "1", borderRadius: 4, background: "#202c33", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <audio ref={audioRef} src={src}
        onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
      />
      <svg width="84" height="84" viewBox="0 0 100 100" style={{ position: "absolute" }}>
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="#25d366" strokeWidth="4"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform={`rotate(-90 ${CX} ${CY})`}
          style={{ transition: "stroke-dashoffset 0.25s linear" }}
        />
      </svg>
      <Button type="text" icon={playing ? <PauseCircleOutlined style={{ fontSize: 26 }} /> : <PlayCircleOutlined style={{ fontSize: 26 }} />}
        onClick={togglePlay}
        style={{ color: "#e9edef", zIndex: 1, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
      />
      {ts && (
        <span style={{ position: "absolute", bottom: 5, right: 6, fontSize: 9, color: "#667781" }}>
          {fmtShortDateTime(ts)}
        </span>
      )}
      <OwnerBadge isOut={isOut} />
    </div>
  );
}

// ── Gallery Thumb ─────────────────────────────────────────────────────────────

export function GalleryThumb({ item, onViewInChat }) {
  const type = (item.type || "").toLowerCase();
  const rawUrl = item.data || null;
  const scrollCtx = useContext(MediaScrollCtx);
  const cachedEntry = rawUrl ? _mediaCache.get(rawUrl) : null;
  const initialUrl = cachedEntry && cachedEntry.expiresAt > Date.now() + 60_000 ? cachedEntry.signedUrl : null;
  const [signedUrl, setSignedUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const ref = useRef(null);
  const ts = item.timestamp ? dayjs(parseInt(item.timestamp) * 1000) : null;

  useEffect(() => {
    if (!rawUrl || signedUrl) return;
    const el = ref.current; const root = scrollCtx?.current || null;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      setLoading(true);
      _resolveMediaUrl(rawUrl).then(url => { setSignedUrl(url); setLoading(false); });
    }, { root, threshold: 0, rootMargin: "200px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [rawUrl, signedUrl]); // eslint-disable-line

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!rawUrl || downloading) return;
    setDownloading(true);
    axios.post(
      API_DOMAIN + "/wati_messages/download_wati_media_file",
      { file_name: rawUrl },
      { headers: { accessToken: localStorage.getItem("accessToken") }, responseType: "blob" }
    ).then(res => {
      const cd = res.headers["content-disposition"] || "";
      const nameMatch = cd.match(/filename="?([^";\n]+)"?/i);
      const fileName = nameMatch?.[1] || item.fileName || rawUrl.split("/").pop() || "download";
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fileName;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(blobUrl);
    }).catch(() => message.error("Download failed."))
      .finally(() => setDownloading(false));
  };

  const eyeBtn = (
    <button onClick={e => { e.stopPropagation(); onViewInChat?.(item.id); }}
      style={{ position: "absolute", top: 5, right: 5, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.55)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
      <EyeOutlined style={{ fontSize: 11 }} />
    </button>
  );

  if (type === "audio") return (
    <div ref={ref} style={{ aspectRatio: "1", borderRadius: 4, overflow: "hidden", position: "relative" }}>
      {loading ? (
        <div style={{ width: "100%", height: "100%", background: "#202c33", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin size="small" />
        </div>
      ) : signedUrl ? (
        <GalleryAudioCard src={signedUrl} ts={ts} isOut={item.owner === true} />
      ) : (
        <div style={{ width: "100%", height: "100%", background: "#202c33", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <AudioOutlined style={{ fontSize: 28, color: "#8696a0" }} />
          <span style={{ fontSize: 10, color: "#8696a0" }}>Voice message</span>
        </div>
      )}
      {eyeBtn}
    </div>
  );

  if (type === "document") {
    const { Icon, color, ext } = getDocMeta(item.fileName);
    return (
      <div ref={ref} style={{ aspectRatio: "1", borderRadius: 4, background: "#202c33", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, overflow: "hidden", padding: 8 }}>
        <Icon style={{ fontSize: 36, color }} />
        <span style={{ fontSize: 10, fontWeight: 700, color, letterSpacing: "0.5px" }}>{ext}</span>
        <span style={{ fontSize: 10, color: "#8696a0", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "90%" }}>
          {item.fileName || "Document"}
        </span>
        {ts && <span style={{ fontSize: 9, color: "#667781" }}>{fmtShortDateTime(ts)}</span>}
        <OwnerBadge isOut={item.owner === true} />
        {eyeBtn}
        <button onClick={handleDownload} disabled={downloading}
          style={{ position: "absolute", bottom: 6, right: 6, width: 26, height: 26, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", cursor: downloading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {downloading ? <Spin size="small" /> : <DownloadOutlined style={{ fontSize: 13 }} />}
        </button>
      </div>
    );
  }

  // image / video — square grid tile
  return (
    <div ref={ref} onClick={() => signedUrl && type === "video" && setVideoOpen(true)}
      style={{ aspectRatio: "1", overflow: "hidden", borderRadius: 4, background: "#202c33", cursor: signedUrl ? "pointer" : "default", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {loading && <Spin size="small" />}
      {!loading && !signedUrl && (type === "image"
        ? <PictureOutlined style={{ fontSize: 22, color: "#8696a0" }} />
        : <PlayCircleOutlined style={{ fontSize: 22, color: "#8696a0" }} />)}
      {!loading && signedUrl && type === "image" && (
        <Image src={signedUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }}
          wrapperStyle={{ width: "100%", height: "100%" }} preview={{ mask: false }} />
      )}
      {!loading && signedUrl && type === "video" && (
        <video src={signedUrl} preload="metadata"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }} />
      )}
      {type === "video" && signedUrl && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}>
          <PlayCircleOutlined style={{ fontSize: 26, color: "#fff" }} />
        </div>
      )}
      {ts && (
        <span style={{ position: "absolute", bottom: 3, right: 5, fontSize: 9, color: "rgba(255,255,255,0.75)", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
          {fmtShortDateTime(ts)}
        </span>
      )}
      <OwnerBadge isOut={item.owner === true} />
      {eyeBtn}
      {type === "video" && (
        <Modal open={videoOpen} onCancel={() => setVideoOpen(false)} footer={null} centered destroyOnClose
          width={Math.min(window.innerWidth * 0.88, 900)}
          styles={{ body: { padding: 0, background: "#000", borderRadius: 8, overflow: "hidden" }, mask: { backdropFilter: "blur(4px)" } }}
          closable={false}>
          {videoOpen && <video src={signedUrl} controls autoPlay style={{ width: "100%", maxHeight: "75vh", display: "block", outline: "none" }} />}
        </Modal>
      )}
    </div>
  );
}

// ── Emoji Picker ──────────────────────────────────────────────────────────────

const RECENT_REACTIONS_KEY = "uzd_reaction_recents";
export function getRecentReactions() {
  try { return JSON.parse(localStorage.getItem(RECENT_REACTIONS_KEY) || "[]"); } catch { return []; }
}
export function addRecentReaction(emoji) {
  const list = getRecentReactions().filter(e => e !== emoji);
  list.unshift(emoji);
  localStorage.setItem(RECENT_REACTIONS_KEY, JSON.stringify(list.slice(0, 20)));
}

export const EMOJI_CATS = [
  { icon: "😊", name: "Smileys", emojis: ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩","😘","😗","☺️","😚","😙","🥲","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔","🤐","🤨","😐","😑","😶","😏","😒","🙄","😬","🤥","😌","😔","😪","🤤","😴","😷","🤒","🤕","🤢","🤮","🤧","🥵","🥶","🥴","😵","🤯","🤠","🥳","🥸","😎","🤓","🧐","😕","😟","🙁","☹️","😮","😯","😲","😳","🥺","😦","😧","😨","😰","😥","😢","😭","😱","😖","😣","😞","😓","😩","😫","🥱","😤","😡","😠","🤬","😈","👿"] },
  { icon: "👋", name: "People", emojis: ["👋","🤚","✋","🖖","👌","🤌","🤏","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️","👍","👎","✊","👊","🤛","🤜","👏","🙌","🫶","👐","🤲","🙏","✍️","💅","🤳","💪","🦾","🦵","🦶","👂","🦻","👃","👁","👅","👄","🫦","👶","👦","👧","🧒","🧔","👩","👨","🧓","👴","👵","🧑","🧑‍⚕️","🧑‍🎓","🧑‍🏫","🧑‍🍳","🧑‍🔧","🧑‍💻","🧑‍🎨","🧑‍✈️","🧑‍🚀","🕵️","💂","🥷","👷","🫅","🤴","👸","🧙","🧝","🧛","🧟","🧞","🧜","🧚","👼","🎅","🤶"] },
  { icon: "🐶", name: "Animals", emojis: ["🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐨","🐯","🦁","🐮","🐷","🐸","🐵","🐔","🐧","🐦","🐤","🦆","🦅","🦉","🦇","🐺","🐗","🐴","🦄","🐝","🪱","🐛","🦋","🐌","🐞","🐜","🦟","🦗","🕷","🦂","🐢","🐍","🦎","🐙","🦑","🦐","🦞","🦀","🐡","🐟","🐠","🐬","🐳","🐋","🦈","🐊","🐅","🐆","🦓","🦍","🦧","🐘","🦛","🦏","🐪","🐫","🦒","🦘","🦬","🐃","🐂","🐄","🐎","🐖","🐏","🐑","🦙","🐐","🦌","🐕","🐩","🦮","🐈","🐓","🦃","🦤","🦚","🦜","🦢","🦩","🕊","🐇","🦝","🦨","🦡","🦫","🦦","🦥","🐁","🐀","🦔","🐾"] },
  { icon: "🍎", name: "Food", emojis: ["🍎","🍐","🍊","🍋","🍌","🍉","🍇","🍓","🫐","🍈","🍒","🍑","🥭","🍍","🥥","🥝","🍅","🍆","🥑","🥦","🥬","🥒","🌶️","🫑","🧄","🧅","🥔","🍠","🥐","🥯","🍞","🥖","🧀","🥚","🍳","🧈","🥞","🧇","🥓","🥩","🍗","🍖","🌮","🌯","🫔","🥙","🧆","🍱","🍘","🍣","🍛","🍜","🍝","🍢","🍡","🍧","🍨","🍦","🥧","🧁","🍰","🎂","🍮","🍭","🍬","🍫","🍿","🍩","🍪","🌰","🥜","🍯","🧃","🥤","🧋","☕","🍵","🫖","🍺","🍻","🥂","🍷","🥃","🍸","🍹","🧉","🍾"] },
  { icon: "⚽", name: "Activities", emojis: ["⚽","🏀","🏈","⚾","🥎","🏐","🏉","🥏","🎾","🪃","🏸","🏒","🥍","🏏","🎯","🏹","🎣","🤿","🥊","🥋","🎽","🛹","🛼","🛷","⛸","🎿","🪂","⛷","🏋️","🤺","🏊","🚴","🏇","🧘","🤸","⛹","🏄","🚵","🎖","🏆","🥇","🥈","🥉","🎗","🎫","🎟","🎪","🎭","🎨","🎰","🎲","🧩","🎮","🕹","♟","🎬","🎤","🎧","🎼","🎹","🪘","🥁","🪗","🎷","🎺","🎸","🎻"] },
  { icon: "🌍", name: "Travel", emojis: ["🌍","🌎","🌏","🗺","🧭","🏔","⛰","🌋","🗻","🏕","🏖","🏜","🏝","🏞","🏟","🏛","🏗","🏠","🏡","🏢","🏥","🏦","🏨","🏪","🏫","🗼","🗽","⛪","🕌","🛕","🕍","⛩","🕋","⛲","⛺","🌁","🌃","🏙","🌄","🌅","🌆","🌇","🌉","🚗","🚕","🚙","🚌","🏎","🚓","🚑","🚒","🚚","🚜","🏍","🛵","🚲","🛴","🛹","⛽","🚦","🚥","✈️","🛫","🛬","🪂","💺","🚁","🚂","🚆","🚇","🚉","⛵","🚤","🛶","⛴","🚀","🛸"] },
  { icon: "💡", name: "Objects", emojis: ["⌚","📱","📲","💻","⌨️","🖥","🖨","🖱","💾","💿","📷","📸","📹","🎥","📽","🎞","📞","☎️","📺","📻","🧭","⏱","⏲","⏰","🕰","⌛","⏳","📡","🔋","🔌","💡","🔦","🕯","🧲","💰","💳","🧾","🔑","🗝","🔐","🔏","🔓","🔒","🗑","🛠","⚙️","🔗","📎","📌","📍","✂️","🖊","✒️","🔬","🔭","📋","📁","📂","📚","📖","🏷","🎁","🎀","🎊","🎉","🎈","🎏","🧸","🛍","🪄"] },
  { icon: "❤️", name: "Symbols", emojis: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","❤️‍🔥","❤️‍🩹","💕","💞","💓","💗","💖","💘","💝","💟","☮️","✝️","☪️","🕉","✡️","🔯","☯️","♾","⚛️","🆘","⚠️","☢️","☣️","✅","❎","🚫","⛔","💯","✔️","❌","❓","❗","‼️","⁉️","🔴","🟠","🟡","🟢","🔵","🟣","⚫","⚪","🟤","⬆️","↗️","➡️","↘️","⬇️","⬅️","↕️","↔️","↩️","↪️","⤴️","⤵️","🔔","🔕","🎵","🎶","🔁","🔂","▶️","⏸","⏹","⏺","⏭","⏮","⏩","⏪","🔈","🔊","📢","📣"] },
];

export function EmojiPickerPanel({ onSelect, onClose }) {
  const recents = getRecentReactions();
  const cats = recents.length > 0
    ? [{ icon: "🕐", name: "Recent", emojis: recents }, ...EMOJI_CATS]
    : EMOJI_CATS;
  const [activeCat, setActiveCat] = useState(0);
  return (
    <div style={{ width: 300, userSelect: "none" }}>
      <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #f0f0f0", padding: "4px 6px 4px" }}>
        <div style={{ display: "flex", gap: 1, overflowX: "auto", flex: 1, scrollbarWidth: "none" }}>
          {cats.map((cat, i) => (
            <button key={cat.name} onClick={() => setActiveCat(i)} title={cat.name}
              style={{ background: i === activeCat ? "#f0f0f0" : "transparent", border: "none", borderRadius: 6, padding: "3px 5px", cursor: "pointer", fontSize: 17, flexShrink: 0, lineHeight: 1, transition: "background 0.1s" }}>
              {cat.icon}
            </button>
          ))}
        </div>
        <button onClick={onClose} title="Close"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "#8c8c8c", fontSize: 13, fontWeight: 700, padding: "2px 6px", marginLeft: 4, borderRadius: 4, flexShrink: 0, lineHeight: 1 }}>
          ✕
        </button>
      </div>
      <div style={{ padding: "3px 6px 2px", fontSize: 9, fontWeight: 700, color: "#8c8c8c", letterSpacing: 0.8, textTransform: "uppercase" }}>
        {cats[activeCat].name}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", padding: "0 4px 6px", maxHeight: 228, overflowY: "auto" }}>
        {cats[activeCat].emojis.map((emoji, i) => (
          <button key={i} onClick={() => onSelect(emoji)}
            style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 20, padding: "3px 2px", borderRadius: 4, lineHeight: 1.1, transition: "background 0.1s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#f5f5f5"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}
